import { useEffect, useId, useRef, useState } from 'react';
import { INK, LINE, MUTED, SKY } from './booking-ui';

/**
 * A dropdown that looks like the rest of the form.
 *
 * A native <select> opens the operating system's own menu, which on macOS is a grey
 * translucent panel with a system highlight that no stylesheet can reach. This is the
 * listbox pattern instead: a button that reports its value, and a list of options that
 * opens under it. Keyboard behaviour follows the WAI-ARIA select-only combobox: arrows
 * move, Enter or Space picks, Escape closes, Home and End jump, and typing a letter
 * moves to the next option starting with it.
 *
 * `placeholder` is the empty choice. Picking it sends back an empty string, so an
 * optional field can be cleared after it was set.
 */
export function SelectField(props: {
  id: string;
  label: string;
  value: string;
  options: string[];
  placeholder: string;
  onChange: (value: string) => void;
  hint?: string;
}) {
  const { id, label, value, options, placeholder, onChange, hint } = props;
  const listId = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const choices = [placeholder, ...options];
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);

  const selectedIndex = value ? choices.indexOf(value) : 0;

  // A click anywhere else closes the list, the way a native menu does.
  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  // Keep the highlighted option in view while arrowing through a long list.
  useEffect(() => {
    if (!open) return;
    const el = document.getElementById(`${listId}-${active}`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [open, active, listId]);

  function openList() {
    setActive(selectedIndex < 0 ? 0 : selectedIndex);
    setOpen(true);
  }

  function pick(index: number) {
    onChange(index === 0 ? '' : choices[index]);
    setOpen(false);
    button.current?.focus();
  }

  function onKeyDown(event: React.KeyboardEvent) {
    const last = choices.length - 1;
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        openList();
      }
      return;
    }
    if (event.key === 'ArrowDown') { event.preventDefault(); setActive((a) => Math.min(last, a + 1)); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
    else if (event.key === 'Home') { event.preventDefault(); setActive(0); }
    else if (event.key === 'End') { event.preventDefault(); setActive(last); }
    else if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); pick(active); }
    else if (event.key === 'Escape') { event.preventDefault(); setOpen(false); }
    else if (event.key === 'Tab') { setOpen(false); }
    else if (event.key.length === 1) {
      const letter = event.key.toLowerCase();
      const order = [...choices.keys()].slice(active + 1).concat([...choices.keys()].slice(0, active + 1));
      const next = order.find((i) => i > 0 && choices[i].toLowerCase().startsWith(letter));
      if (next !== undefined) setActive(next);
    }
  }

  return (
    <div ref={wrap} className="bk-select" style={{ marginBottom: 14, position: 'relative' }}>
      <label id={`${id}-label`} htmlFor={id} style={{ display: 'block', marginBottom: 6, fontSize: 14, color: MUTED }}>{label}</label>
      <button
        ref={button}
        id={id}
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-labelledby={`${id}-label ${id}`}
        aria-describedby={hintId}
        aria-activedescendant={open ? `${listId}-${active}` : undefined}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
        className="bk-select__button"
        data-open={open || undefined}
        style={{ color: value ? INK : MUTED, borderColor: open ? SKY : LINE }}
      >
        <span>{value || placeholder}</span>
        <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18"><path d="M6 9l6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      <ul id={listId} role="listbox" aria-labelledby={`${id}-label`} className="bk-select__list" hidden={!open}>
        {choices.map((choice, index) => (
          <li
            key={choice}
            id={`${listId}-${index}`}
            role="option"
            aria-selected={index === selectedIndex}
            data-active={index === active || undefined}
            className="bk-select__option"
            onMouseEnter={() => setActive(index)}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => pick(index)}
          >
            <span>{choice}</span>
            {index === selectedIndex && <svg viewBox="0 0 24 24" aria-hidden="true" width="16" height="16"><path d="M5 12l5 5 9-10" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
          </li>
        ))}
      </ul>
      {hint && <p id={hintId} style={{ marginTop: 6, fontSize: 13, color: MUTED }}>{hint}</p>}
    </div>
  );
}
